import type { LessonDefinition, MilestoneDefinition, UnitDefinition } from './types.ts';

/**
 * Note-reading curriculum v1. Everything here is supported by the
 * note-identification exercise family. Staff positions: 0 = bottom line,
 * 8 = top line, negative = below the staff.
 *
 * Content ids are stable. To change a lesson, bump its `version`;
 * `curriculum.lock.json` + tests enforce this.
 */

export const MILESTONES: readonly MilestoneDefinition[] = [
  { id: 'staff-reader', title: 'Staff reader', description: 'Named every natural note on the five-line staff.' },
  { id: 'ledger-reader', title: 'Ledger-line reader', description: 'Read notes up to three ledger lines above and below the staff.' },
  { id: 'accidental-reader', title: 'Accidental reader', description: 'Read sharps and flats across the staff.' },
  { id: 'note-reading-graduate', title: 'Note-reading graduate', description: 'Passed the cumulative note-reading challenge.' },
];

export const UNITS: readonly UnitDefinition[] = [
  {
    id: 'u1-staff-basics',
    title: 'Staff and clef basics',
    summary: 'How the staff works and the landmark note your clef gives you.',
    lessonIds: ['nr-u1-meet-the-staff', 'nr-u1-clef-neighbors'],
  },
  {
    id: 'u2-natural-groups',
    title: 'Natural notes in small groups',
    summary: 'Learn the staff a few notes at a time.',
    lessonIds: ['nr-u2-lower-staff', 'nr-u2-upper-staff', 'nr-u2-whole-staff'],
  },
  {
    id: 'u3-lines-spaces',
    title: 'Lines and spaces',
    summary: 'Tell line notes from space notes quickly and name them.',
    lessonIds: ['nr-u3-lines', 'nr-u3-spaces', 'nr-u3-mixed'],
  },
  {
    id: 'u4-expanded-range',
    title: 'Just beyond the staff',
    summary: 'The first ledger line and the spaces around it.',
    lessonIds: ['nr-u4-first-below', 'nr-u4-first-above', 'nr-u4-expanded-review'],
  },
  {
    id: 'u5-ledger-lines',
    title: 'Ledger lines',
    summary: 'Read up to three ledger lines above and below the staff.',
    lessonIds: ['nr-u5-ledger-below', 'nr-u5-ledger-above', 'nr-u5-full-range'],
  },
  {
    id: 'u6-octaves',
    title: 'Octave names (optional)',
    summary: 'Name notes with octave numbers, where middle C is C4.',
    optional: true,
    lessonIds: ['nr-u6-octave-numbers', 'nr-u6-octaves-full-range'],
  },
  {
    id: 'u7-accidentals',
    title: 'Sharps and flats',
    summary: 'Read notes with sharps and flats, and spell them correctly.',
    lessonIds: ['nr-u7-sharps', 'nr-u7-flats', 'nr-u7-sharps-and-flats', 'nr-u7-accidentals-wide'],
  },
  {
    id: 'u8-review',
    title: 'Cumulative review',
    summary: 'Bring everything together.',
    lessonIds: ['nr-u8-mixed-review', 'nr-u8-challenge'],
  },
];

const STAFF = { from: 0, to: 8 };

export const LESSONS: readonly LessonDefinition[] = [
  {
    id: 'nr-u1-meet-the-staff',
    version: 1,
    unitId: 'u1-staff-basics',
    title: 'Meet the staff',
    summary: 'Five lines, four spaces, and your clef’s landmark line.',
    prerequisites: [],
    intro: [
      {
        title: 'Five lines, four spaces',
        body: 'Music is written on a staff of five horizontal lines. Count lines and spaces from the bottom up: the bottom line is the first line, and the space above it is the first space. Higher on the staff means higher in pitch.',
        example: { positions: [0, 2, 4, 6, 8], caption: 'A note on each line, from the first (bottom) line to the fifth (top) line.' },
      },
      {
        title: 'Seven letter names',
        body: 'Notes use the letters A B C D E F G, then start again at A. Each step up the staff — line to the next space, space to the next line — moves one letter forward.',
      },
      {
        title: 'Your clef is a map key',
        body: '{landmarkSentence} Once you know one line, you can find every other note by counting steps.',
        example: { positions: ['landmark'], caption: 'The landmark note for {clef} clef: {landmark}.' },
      },
    ],
    exercise: { type: 'note-identification', positions: ['landmark-2', 'landmark', 'landmark+2'], staffFilter: 'all', accidentals: ['natural'], answerMode: 'letter', questionCount: 8 },
    passAccuracy: 0.75,
    skillKinds: ['lines'],
  },
  {
    id: 'nr-u1-clef-neighbors',
    version: 1,
    unitId: 'u1-staff-basics',
    title: 'Step from the landmark',
    summary: 'Name the notes one step above and below your clef’s landmark.',
    prerequisites: ['nr-u1-meet-the-staff'],
    intro: [
      {
        title: 'Steps alternate line, space, line',
        body: 'The note just above a line sits in the space above it, and it is the next letter. The note just below a line is the letter before. From {landmark}, one step up is {landmarkUp} and one step down is {landmarkDown}.',
        example: { positions: ['landmark-1', 'landmark', 'landmark+1'], caption: '{landmarkDown}, {landmark}, {landmarkUp}' },
      },
    ],
    exercise: { type: 'note-identification', positions: ['landmark-2', 'landmark-1', 'landmark', 'landmark+1', 'landmark+2'], staffFilter: 'all', accidentals: ['natural'], answerMode: 'letter', questionCount: 10 },
    passAccuracy: 0.8,
    skillKinds: ['lines', 'spaces'],
  },
  {
    id: 'nr-u2-lower-staff',
    version: 1,
    unitId: 'u2-natural-groups',
    title: 'The lower staff',
    summary: 'The bottom lines and spaces.',
    prerequisites: ['nr-u1-clef-neighbors'],
    intro: [
      {
        title: 'Start at the bottom line',
        body: 'In {clef} clef the bottom line is {bottomLine}. Step upward letter by letter to reach the middle line.',
        example: { positions: [0, 1, 2, 3, 4], caption: 'The lower half of the {clef} staff.' },
      },
    ],
    exercise: { type: 'note-identification', positions: { from: 0, to: 4 }, staffFilter: 'all', accidentals: ['natural'], answerMode: 'letter', questionCount: 10 },
    passAccuracy: 0.8,
    skillKinds: ['lines', 'spaces'],
  },
  {
    id: 'nr-u2-upper-staff',
    version: 1,
    unitId: 'u2-natural-groups',
    title: 'The upper staff',
    summary: 'From the middle line to the top line.',
    prerequisites: ['nr-u2-lower-staff'],
    intro: [
      {
        title: 'Up to the top line',
        body: 'The top line of the {clef} staff is {topLine}. Counting down from the top line is just as useful as counting up from the bottom.',
        example: { positions: [4, 5, 6, 7, 8], caption: 'The upper half of the {clef} staff.' },
      },
    ],
    exercise: { type: 'note-identification', positions: { from: 4, to: 8 }, staffFilter: 'all', accidentals: ['natural'], answerMode: 'letter', questionCount: 10 },
    passAccuracy: 0.8,
    skillKinds: ['lines', 'spaces'],
  },
  {
    id: 'nr-u2-whole-staff',
    version: 1,
    unitId: 'u2-natural-groups',
    title: 'The whole staff',
    summary: 'Every natural note from the bottom line to the top line.',
    prerequisites: ['nr-u2-upper-staff'],
    milestone: 'staff-reader',
    intro: [
      {
        title: 'Nine notes on five lines',
        body: 'Five lines and four spaces give nine notes, from {bottomLine} to {topLine}. Use whichever landmark is closest — the bottom line, the top line, or your clef line — and count the fewest steps.',
        example: { positions: [0, 1, 2, 3, 4, 5, 6, 7, 8], caption: 'All nine staff notes in {clef} clef.' },
      },
    ],
    exercise: { type: 'note-identification', positions: STAFF, staffFilter: 'all', accidentals: ['natural'], answerMode: 'letter', questionCount: 12 },
    passAccuracy: 0.8,
    skillKinds: ['lines', 'spaces'],
  },
  {
    id: 'nr-u3-lines',
    version: 1,
    unitId: 'u3-lines-spaces',
    title: 'Line notes',
    summary: 'Name the five line notes on sight.',
    prerequisites: ['nr-u2-whole-staff'],
    intro: [
      {
        title: 'Lines skip a letter',
        body: 'Line notes are two steps apart, so they skip every other letter. In {clef} clef the lines are {lines}, bottom to top. Make up your own sentence from those letters to remember them.',
        example: { positions: [0, 2, 4, 6, 8], caption: 'Lines: {lines}' },
      },
    ],
    exercise: { type: 'note-identification', positions: STAFF, staffFilter: 'lines', accidentals: ['natural'], answerMode: 'letter', questionCount: 10 },
    passAccuracy: 0.85,
    skillKinds: ['lines'],
  },
  {
    id: 'nr-u3-spaces',
    version: 1,
    unitId: 'u3-lines-spaces',
    title: 'Space notes',
    summary: 'Name the spaces, including just above and below the staff.',
    prerequisites: ['nr-u3-lines'],
    intro: [
      {
        title: 'Spaces fill the gaps',
        body: 'The four spaces inside the {clef} staff are {spaces}, bottom to top. A note can also sit in the space just below the bottom line or just above the top line without any extra lines.',
        example: { positions: [-1, 1, 3, 5, 7, 9], caption: 'Spaces, including just outside the staff.' },
      },
    ],
    exercise: { type: 'note-identification', positions: { from: -1, to: 9 }, staffFilter: 'spaces', accidentals: ['natural'], answerMode: 'letter', questionCount: 10 },
    passAccuracy: 0.85,
    skillKinds: ['spaces'],
  },
  {
    id: 'nr-u3-mixed',
    version: 1,
    unitId: 'u3-lines-spaces',
    title: 'Lines and spaces together',
    summary: 'Decide line or space first, then name the note.',
    prerequisites: ['nr-u3-spaces'],
    intro: [
      {
        title: 'Line or space?',
        body: 'A line runs through the middle of a line note. A space note sits between two lines, touching both. Deciding which one you see first cuts your choices in half.',
      },
    ],
    exercise: { type: 'note-identification', positions: { from: -1, to: 9 }, staffFilter: 'all', accidentals: ['natural'], answerMode: 'letter', questionCount: 12 },
    passAccuracy: 0.85,
    skillKinds: ['lines', 'spaces'],
  },
  {
    id: 'nr-u4-first-below',
    version: 1,
    unitId: 'u4-expanded-range',
    title: 'One ledger line below',
    summary: 'The first ledger line under the staff and the space below it.',
    prerequisites: ['nr-u3-mixed'],
    intro: [
      {
        title: 'Ledger lines extend the staff',
        body: 'A ledger line is a short extra line for one note. It continues the staff pattern: the first ledger line below the {clef} staff is two steps below the bottom line ({bottomLine}).',
        example: { positions: [-3, -2, -1, 0], caption: 'Below the {clef} staff.' },
      },
    ],
    exercise: { type: 'note-identification', positions: { from: -3, to: 2 }, staffFilter: 'all', accidentals: ['natural'], answerMode: 'letter', questionCount: 10 },
    passAccuracy: 0.8,
    skillKinds: ['ledger-below', 'spaces', 'lines'],
  },
  {
    id: 'nr-u4-first-above',
    version: 1,
    unitId: 'u4-expanded-range',
    title: 'One ledger line above',
    summary: 'The first ledger line over the staff and the space above it.',
    prerequisites: ['nr-u4-first-below'],
    intro: [
      {
        title: 'Above the top line',
        body: 'The first ledger line above the {clef} staff is two steps above the top line ({topLine}). The space above that ledger line is the next letter again.',
        example: { positions: [8, 9, 10, 11], caption: 'Above the {clef} staff.' },
      },
    ],
    exercise: { type: 'note-identification', positions: { from: 6, to: 11 }, staffFilter: 'all', accidentals: ['natural'], answerMode: 'letter', questionCount: 10 },
    passAccuracy: 0.8,
    skillKinds: ['ledger-above', 'spaces', 'lines'],
  },
  {
    id: 'nr-u4-expanded-review',
    version: 1,
    unitId: 'u4-expanded-range',
    title: 'The expanded staff',
    summary: 'Everything from one ledger line below to one ledger line above.',
    prerequisites: ['nr-u4-first-above'],
    intro: [
      {
        title: 'Middle C in your clef',
        body: 'Middle C (C4) is a handy shared landmark between clefs. {middleCSentence}',
        example: { positions: ['middleC'], caption: 'Middle C in {clef} clef.' },
      },
    ],
    exercise: { type: 'note-identification', positions: { from: -3, to: 11 }, staffFilter: 'all', accidentals: ['natural'], answerMode: 'letter', questionCount: 12 },
    passAccuracy: 0.8,
    skillKinds: ['ledger-below', 'ledger-above', 'lines', 'spaces'],
  },
  {
    id: 'nr-u5-ledger-below',
    version: 1,
    unitId: 'u5-ledger-lines',
    title: 'Ledger lines below',
    summary: 'Up to three ledger lines under the staff.',
    prerequisites: ['nr-u4-expanded-review'],
    intro: [
      {
        title: 'Count the ledger lines',
        body: 'Each ledger line is two steps lower than the one above it, just like staff lines. Count ledger lines from the staff outward: one, two, three. A note hanging under a ledger line is in the space below it.',
        example: { positions: [-2, -4, -6], caption: 'First, second and third ledger lines below the {clef} staff.' },
      },
    ],
    exercise: { type: 'note-identification', positions: { from: -7, to: 0 }, staffFilter: 'all', accidentals: ['natural'], answerMode: 'letter', questionCount: 12 },
    passAccuracy: 0.8,
    skillKinds: ['ledger-below'],
  },
  {
    id: 'nr-u5-ledger-above',
    version: 1,
    unitId: 'u5-ledger-lines',
    title: 'Ledger lines above',
    summary: 'Up to three ledger lines over the staff.',
    prerequisites: ['nr-u5-ledger-below'],
    intro: [
      {
        title: 'Climbing above the staff',
        body: 'Above the staff, ledger lines are again two steps apart. The line notes above the {clef} staff continue the same skip-a-letter pattern as the staff lines.',
        example: { positions: [10, 12, 14], caption: 'First, second and third ledger lines above the {clef} staff.' },
      },
    ],
    exercise: { type: 'note-identification', positions: { from: 8, to: 15 }, staffFilter: 'all', accidentals: ['natural'], answerMode: 'letter', questionCount: 12 },
    passAccuracy: 0.8,
    skillKinds: ['ledger-above'],
  },
  {
    id: 'nr-u5-full-range',
    version: 1,
    unitId: 'u5-ledger-lines',
    title: 'The full range',
    summary: 'Every natural note within three ledger lines.',
    prerequisites: ['nr-u5-ledger-above'],
    milestone: 'ledger-reader',
    intro: [
      {
        title: 'Pick the nearest landmark',
        body: 'Far from the staff, count from the nearest landmark instead of from your clef line: the bottom line, the top line, or middle C.',
      },
    ],
    exercise: { type: 'note-identification', positions: { from: -7, to: 15 }, staffFilter: 'all', accidentals: ['natural'], answerMode: 'letter', questionCount: 15 },
    passAccuracy: 0.8,
    skillKinds: ['ledger-below', 'ledger-above', 'lines', 'spaces'],
  },
  {
    id: 'nr-u6-octave-numbers',
    version: 1,
    unitId: 'u6-octaves',
    title: 'Octave numbers',
    summary: 'Add octave numbers to staff notes.',
    prerequisites: ['nr-u5-full-range'],
    optional: true,
    intro: [
      {
        title: 'Scientific pitch names',
        body: 'Octave numbers tell you exactly which C, D or E you mean. Middle C is C4. The number goes up at every C: the B just below middle C is B3, and the D just above it is D4.',
        example: { positions: ['middleC-1', 'middleC', 'middleC+1'], caption: 'B3, C4 (middle C), D4.' },
      },
    ],
    exercise: { type: 'note-identification', positions: STAFF, staffFilter: 'all', accidentals: ['natural'], answerMode: 'spelled-octave', questionCount: 10 },
    passAccuracy: 0.8,
    skillKinds: ['octaves'],
  },
  {
    id: 'nr-u6-octaves-full-range',
    version: 1,
    unitId: 'u6-octaves',
    title: 'Octaves across the range',
    summary: 'Octave numbers with ledger lines.',
    prerequisites: ['nr-u6-octave-numbers'],
    optional: true,
    intro: [
      {
        title: 'Watch the C',
        body: 'When you count past a C, the octave number changes. Going up from B3 you reach C4; going down from C4 you reach B3.',
      },
    ],
    exercise: { type: 'note-identification', positions: { from: -7, to: 15 }, staffFilter: 'all', accidentals: ['natural'], answerMode: 'spelled-octave', questionCount: 12 },
    passAccuracy: 0.8,
    skillKinds: ['octaves'],
  },
  {
    id: 'nr-u7-sharps',
    version: 1,
    unitId: 'u7-accidentals',
    title: 'Sharps',
    summary: 'A sharp raises a note by a half step.',
    prerequisites: ['nr-u5-full-range'],
    intro: [
      {
        title: 'The sharp sign ♯',
        body: 'A sharp written in front of a note raises it by a half step. The note keeps its line or space — and its letter — so a sharp on the {landmark} line is {landmarkLetter}♯. Name the letter first, then add “sharp”.',
        example: { positions: ['landmark'], accidentals: ['sharp'], caption: '{landmarkLetter}♯ in {clef} clef.' },
      },
    ],
    exercise: { type: 'note-identification', positions: STAFF, staffFilter: 'all', accidentals: ['natural', 'sharp'], answerMode: 'spelled', questionCount: 12, accidentalRate: 0.6 },
    passAccuracy: 0.8,
    skillKinds: ['sharps'],
  },
  {
    id: 'nr-u7-flats',
    version: 1,
    unitId: 'u7-accidentals',
    title: 'Flats',
    summary: 'A flat lowers a note by a half step.',
    prerequisites: ['nr-u7-sharps'],
    intro: [
      {
        title: 'The flat sign ♭',
        body: 'A flat lowers a note by a half step. Like a sharp, it does not move the note to a different line or space: a flat on the {landmark} line is {landmarkLetter}♭.',
        example: { positions: ['landmark'], accidentals: ['flat'], caption: '{landmarkLetter}♭ in {clef} clef.' },
      },
    ],
    exercise: { type: 'note-identification', positions: STAFF, staffFilter: 'all', accidentals: ['natural', 'flat'], answerMode: 'spelled', questionCount: 12, accidentalRate: 0.6 },
    passAccuracy: 0.8,
    skillKinds: ['flats'],
  },
  {
    id: 'nr-u7-sharps-and-flats',
    version: 1,
    unitId: 'u7-accidentals',
    title: 'Sharps and flats',
    summary: 'Spell what is written, even when two names sound alike.',
    prerequisites: ['nr-u7-flats'],
    intro: [
      {
        title: 'Same sound, different name',
        body: 'C♯ and D♭ sound the same on a piano, but they are written differently: C♯ sits on a C line or space, D♭ on a D line or space. Always name the note by where it is written and which sign it has.',
      },
    ],
    exercise: { type: 'note-identification', positions: STAFF, staffFilter: 'all', accidentals: ['natural', 'sharp', 'flat'], answerMode: 'spelled', questionCount: 12, accidentalRate: 0.6 },
    passAccuracy: 0.8,
    skillKinds: ['sharps', 'flats'],
  },
  {
    id: 'nr-u7-accidentals-wide',
    version: 1,
    unitId: 'u7-accidentals',
    title: 'Accidentals beyond the staff',
    summary: 'Sharps and flats with ledger lines.',
    prerequisites: ['nr-u7-sharps-and-flats'],
    milestone: 'accidental-reader',
    intro: [
      {
        title: 'Same rules, more lines',
        body: 'Accidentals work the same way on ledger lines: find the note’s letter from its position, then add the sign.',
      },
    ],
    exercise: { type: 'note-identification', positions: { from: -4, to: 12 }, staffFilter: 'all', accidentals: ['natural', 'sharp', 'flat'], answerMode: 'spelled', questionCount: 12, accidentalRate: 0.5 },
    passAccuracy: 0.8,
    skillKinds: ['sharps', 'flats', 'ledger-below', 'ledger-above'],
  },
  {
    id: 'nr-u8-mixed-review',
    version: 1,
    unitId: 'u8-review',
    title: 'Mixed review',
    summary: 'Naturals and accidentals across the full range.',
    prerequisites: ['nr-u7-accidentals-wide'],
    intro: [
      {
        title: 'Put it together',
        body: 'This review mixes everything: lines, spaces, ledger lines, sharps and flats. Take a breath before each note — accuracy matters more than speed.',
      },
    ],
    exercise: { type: 'note-identification', positions: { from: -7, to: 15 }, staffFilter: 'all', accidentals: ['natural', 'sharp', 'flat'], answerMode: 'spelled', questionCount: 15, accidentalRate: 0.4 },
    passAccuracy: 0.8,
    skillKinds: ['lines', 'spaces', 'ledger-below', 'ledger-above', 'sharps', 'flats'],
  },
  {
    id: 'nr-u8-challenge',
    version: 1,
    unitId: 'u8-review',
    title: 'Note-reading challenge',
    summary: 'Twenty notes at a 90% bar.',
    prerequisites: ['nr-u8-mixed-review'],
    milestone: 'note-reading-graduate',
    intro: [
      {
        title: 'The final check',
        body: 'Twenty notes from anywhere in the range. Reach 90% on your first tries to finish the note-reading path. You can retake it any time.',
      },
    ],
    exercise: { type: 'note-identification', positions: { from: -7, to: 15 }, staffFilter: 'all', accidentals: ['natural', 'sharp', 'flat'], answerMode: 'spelled', questionCount: 20, accidentalRate: 0.4 },
    passAccuracy: 0.9,
    skillKinds: ['lines', 'spaces', 'ledger-below', 'ledger-above', 'sharps', 'flats'],
  },
];
